import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Truck,
  ShieldPlus,
  PackageCheck,
  Boxes,
  Route,
} from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { SEO as META, SERVICES } from "../content";

const ICONS = { Truck, ShieldPlus, PackageCheck, Boxes, Route };

export default function Services() {
  const { hash } = useLocation();

  // Scroll to anchor on mount when arriving via #slug.
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [hash]);

  return (
    <>
      <SEO {...META.services} />

      {/* HERO */}
      <section
        className="relative isolate flex min-h-[55vh] items-center overflow-hidden md:min-h-[60vh]"
        data-testid="services-hero"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/hero-services.jpg"
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-scrim-light" />
        </div>
        <div className="container-pad py-20 md:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/90 backdrop-blur-sm">
                Services
              </span>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Services Built for Precision
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                From same-day local courier to dedicated route contracts —
                Ridgehawk delivers with care, speed, and compliance.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {SERVICES.map((s) => (
                  <a
                    key={s.slug}
                    href={`#${s.slug}`}
                    className="rounded-md border border-white/30 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/85 backdrop-blur-sm transition-colors hover:border-ridge-amber hover:text-ridge-amber"
                    data-testid={`services-jump-${s.slug}`}
                  >
                    {s.short}
                  </a>
                ))}
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <div className="bg-white">
        {SERVICES.map((service, idx) => {
          const C = ICONS[service.iconName] || Truck;
          const reverse = idx % 2 === 1;
          const altBg = idx % 2 === 1;
          return (
            <section
              key={service.slug}
              id={service.slug}
              data-testid={`service-block-${service.slug}`}
              className={`section-pad scroll-mt-24 ${altBg ? "bg-ridge-stone" : "bg-white"}`}
            >
              <div className="container-pad">
                <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16">
                  {/* Image */}
                  <SectionReveal
                    className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}
                  >
                    <div className="relative overflow-hidden rounded-md border border-ridge-border bg-ridge-stone">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
                        loading="lazy"
                      />
                      <div className="pointer-events-none absolute left-0 top-0 px-3 py-1.5">
                        <span className="rounded-md bg-ridge-charcoal/85 px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                          0{idx + 1} / 0{SERVICES.length}
                        </span>
                      </div>
                    </div>
                  </SectionReveal>

                  {/* Content */}
                  <SectionReveal
                    className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}
                    delay={0.1}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                        <C className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ridge-amber">
                        {service.subheadline}
                      </span>
                    </div>
                    <h2 className="font-display mt-5 text-3xl font-bold leading-[1.1] tracking-tight text-ridge-charcoal md:text-4xl">
                      {service.title}
                    </h2>
                    <p className="body-base mt-5 text-ridge-text">
                      {service.longBody}
                    </p>

                    <h3 className="font-display mt-9 text-xs font-bold uppercase tracking-[0.2em] text-ridge-charcoal">
                      Key Features
                    </h3>
                    <ul className="mt-5 space-y-3">
                      {service.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-3 text-sm leading-relaxed text-ridge-text md:text-base"
                        >
                          <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ridge-amber/15 text-ridge-amber">
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-10">
                      <Link
                        to="/quote"
                        className="btn-primary"
                        data-testid={`service-cta-${service.slug}`}
                      >
                        {service.cta}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </SectionReveal>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CLOSING CTA */}
      <section className="bg-ridge-charcoal" data-testid="services-closing-cta">
        <div className="container-pad py-16 md:py-20">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <span className="overline">Not sure which service fits?</span>
              <h2 className="font-display mt-3 text-3xl font-bold leading-tight tracking-tight text-white md:text-4xl">
                Tell us what you need to move — we'll design the right service.
              </h2>
            </div>
            <Link
              to="/quote"
              className="btn-accent"
              data-testid="services-closing-cta-quote"
            >
              Start a Quote
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
